import React from "react";
import { ExternalLink, Info } from "react-feather";
import { SparkleIcon } from "./icons";
import { Spinner } from "../../atoms/Spinner";
import { DELIVER_COPY } from "./deliverStory.copy";
import type { DeliveryAttachmentPreview, DeliveryFormat, Platform } from "./types";
import {
  Wrap,
  PreviewLabel,
  Card,
  InnerCard,
  Header,
  LogoImg,
  BrandName,
  Body,
  Grid,
  Panel,
  SummaryPanel,
  ChartPreview,
  ChartPreviewImg,
  PanelFooter,
  PanelTag,
  SummaryTitle,
  BulletList,
  Bullet,
  Actions,
  PrimaryCta,
  SecondaryCta,
  SecondaryButtonPair,
  ChatCard,
  ChatHeader,
  LogoPillWrap,
  LogoPillImg,
  ChatBrandName,
  MessageText,
  IndentedBlock,
  ThreadBar,
  AttachmentCard,
  AttachmentImage,
  AttachmentFooter,
  AttachmentName,
  AttachmentMeta,
  ChatPrimaryCta,
  SingleChatActionRow,
  InfoHintRow,
  InfoHintText,
  AttachmentStatus,
} from "./DeliveryPreview.styles";

export interface DeliveryPreviewProps {
  platform: Platform;
  /** The user's actual typed message — shown live so the preview matches
   * what will really be sent (previously a hardcoded placeholder, which
   * was misleading). Empty stays empty. */
  message?: string;
  /** The real rendered deck (front page, type, size) when the delivery
   * attaches one; absent → a neutral placeholder named from the format. */
  attachment?: DeliveryAttachmentPreview;
  /** Chosen format; names the attachment when no rendered file exists. */
  format?: DeliveryFormat;
  /** Story AI summary lines; the summary panel is hidden when absent. */
  summaryBullets?: string[];
}

interface AttachmentProps {
  attachment?: DeliveryAttachmentPreview;
  format?: DeliveryFormat;
}

const fallbackName = (format?: DeliveryFormat): string =>
  format ? DELIVER_COPY.previewFormatName[format] : "Attachment";

/** Front page of the rendered deck, or its preparing/error state. */
const AttachmentVisual: React.FC<{
  attachment: DeliveryAttachmentPreview;
  Image: typeof AttachmentImage | typeof ChartPreviewImg;
}> = ({ attachment, Image }) => {
  if (attachment.status === "rendering") {
    return (
      <AttachmentStatus role="status">
        <Spinner size={20} />
        Preparing preview…
      </AttachmentStatus>
    );
  }
  if (attachment.status === "error") {
    return (
      <AttachmentStatus $tone="error" role="alert">
        {attachment.errorMessage}
      </AttachmentStatus>
    );
  }
  return attachment.imageUrl ? (
    <Image src={attachment.imageUrl} alt="Front page of the attachment" />
  ) : null;
};

const AttachmentBlock: React.FC<AttachmentProps> = ({ attachment, format }) =>
  attachment ? (
    <AttachmentCard data-testid="delivery-attachment">
      <AttachmentVisual attachment={attachment} Image={AttachmentImage} />
      <AttachmentFooter>
        <AttachmentName>{attachment.name}</AttachmentName>
        {attachment.sizeLabel && <AttachmentMeta>{attachment.sizeLabel}</AttachmentMeta>}
      </AttachmentFooter>
    </AttachmentCard>
  ) : (
    <AttachmentCard>
      <AttachmentStatus>{DELIVER_COPY.previewNoImage}</AttachmentStatus>
      <AttachmentFooter>
        <AttachmentName>{fallbackName(format)}</AttachmentName>
      </AttachmentFooter>
    </AttachmentCard>
  );

const ChatBrandHeader: React.FC = () => (
  <ChatHeader>
    <LogoPillWrap>
      <LogoPillImg src="/logo.png" alt="MagicBI logo" />
    </LogoPillWrap>
    <ChatBrandName>MagicBI</ChatBrandName>
  </ChatHeader>
);

interface PreviewMessageProps extends AttachmentProps {
  message: string;
  summaryBullets?: string[];
}

const EmailPreview: React.FC<PreviewMessageProps> = ({ message, attachment, format, summaryBullets }) => (
  <Card>
    <Header>
      <LogoImg src="/logo.png" alt="MagicBI logo" />
      <BrandName>MagicBI</BrandName>
    </Header>

    <InnerCard>
      <Body>{message}</Body>

      <Grid>
        {attachment ? (
          <Panel data-testid="delivery-attachment">
            <ChartPreview>
              <AttachmentVisual attachment={attachment} Image={ChartPreviewImg} />
            </ChartPreview>
            <PanelFooter>
              <PanelTag>{attachment.name}</PanelTag>
              {attachment.sizeLabel && <PanelTag>{attachment.sizeLabel}</PanelTag>}
            </PanelFooter>
          </Panel>
        ) : (
          <Panel>
            <ChartPreview>
              <AttachmentStatus>{DELIVER_COPY.previewNoImage}</AttachmentStatus>
            </ChartPreview>
            <PanelFooter>
              <PanelTag>{fallbackName(format)}</PanelTag>
            </PanelFooter>
          </Panel>
        )}
        {summaryBullets && summaryBullets.length > 0 && (
          <SummaryPanel>
            <SummaryTitle>
              <SparkleIcon />
              {DELIVER_COPY.previewSummaryTitle}
            </SummaryTitle>
            <BulletList>
              {summaryBullets.map((b) => (
                <Bullet key={b}>{b}</Bullet>
              ))}
            </BulletList>
          </SummaryPanel>
        )}
      </Grid>

      <Actions>
        <PrimaryCta type="button">View full story on MagicBI</PrimaryCta>
        <SecondaryButtonPair>
          <SecondaryCta type="button">Acknowledge</SecondaryCta>
          <SecondaryCta type="button">Snooze story</SecondaryCta>
        </SecondaryButtonPair>
      </Actions>
    </InnerCard>
  </Card>
);

const SlackPreview: React.FC<PreviewMessageProps> = ({ message, attachment, format }) => (
  // No action buttons here (View in MagicBI / Acknowledge / Snooze story) —
  // the real delivered Slack message never has these, so showing them in
  // the preview was misleading. Email and Teams previews are untouched —
  // this is a Slack-only change.
  //
  // The invite hint is a SEPARATE block below the card (not nested inside
  // the message/attachment card) — it's guidance about the picker, not
  // part of what gets delivered to Slack, so it shouldn't visually read as
  // part of the message content itself.
  <>
    <ChatCard>
      <ChatBrandHeader />
      <IndentedBlock>
        <ThreadBar aria-hidden />
        <MessageText>{message}</MessageText>
        <AttachmentBlock attachment={attachment} format={format} />
      </IndentedBlock>
    </ChatCard>
    <InfoHintRow>
      <Info size={14} />
      <InfoHintText>
        Don&apos;t see a channel you need? Type{" "}
        <strong>/invite @MagicBi-App</strong> in that channel in Slack first,
        then it&apos;ll show up here.
      </InfoHintText>
    </InfoHintRow>
  </>
);

const TeamsPreview: React.FC<PreviewMessageProps> = ({ message, attachment, format }) => (
  <ChatCard>
    <ChatBrandHeader />
    <MessageText>{message}</MessageText>
    <AttachmentBlock attachment={attachment} format={format} />
    <SingleChatActionRow>
      <ChatPrimaryCta type="button">
        <ExternalLink size={14} />
        View in MagicBI
      </ChatPrimaryCta>
    </SingleChatActionRow>
  </ChatCard>
);

const DeliveryPreview: React.FC<DeliveryPreviewProps> = ({
  platform,
  message,
  attachment,
  format,
  summaryBullets,
}) => {
  const resolvedMessage = message ?? "";
  const content =
    platform === "slack" ? (
      <SlackPreview message={resolvedMessage} attachment={attachment} format={format} />
    ) : platform === "teams" ? (
      <TeamsPreview message={resolvedMessage} attachment={attachment} format={format} />
    ) : (
      <EmailPreview
        message={resolvedMessage}
        attachment={attachment}
        format={format}
        summaryBullets={summaryBullets}
      />
    );

  return (
    <Wrap>
      <PreviewLabel>{DELIVER_COPY.previewLabel}</PreviewLabel>
      {content}
    </Wrap>
  );
};

export default DeliveryPreview;
